import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { useQuestionToggleAtom } from '../../atoms/chat/useQuestionToggleAtom';
import { useSelectedTabAtom, selectedTabAtom } from '../../atoms/chat/useSelectedTabAtom';
import {useMessageHistoryAtom, messageHistoryAtomSk, messageHistoryAtomEn, messageHistoryAtomCz} from '../../atoms/chat/useMessageHistoryAtom';
import { allowStopAtom } from '../../atoms/chat/useAllowStopAtom';
import { isStoppingAtom } from '../../atoms/chat/useIsStoppingAtom';
import { useFormButtonEnabledAtom } from '../../atoms/chat/useFormButtonEnabledAtom';
import { useFormInputEnabledAtom} from '../../atoms/chat/useFormInputEnabledAtom';
import {selectedQuestionAtom} from "../../atoms/chat/useSelectedQuestionAtom";
import { useChatEnvironmentAtom } from '../../atoms/chat/useChatEnvironmentAtom';
import { useActiveSessionAtom } from '../../atoms/chat/useActiveSessionAtom';
import { newUuid } from '../../utils/uuid';
import { appenedChatItem } from '../../services/appendChatItem';
import { sendMessageToBE, sendMessageToBEStream, CHAT_INTENT_STREAM_ENDPOINTS } from '../../api/questionAPIs';
import { generateSessionTitle } from '../../api/chatHistoryAPIs';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { useTabItemIdAtom } from '../../atoms/chat/useTabItemIdAtom';

import TextArea from './TextArea';
import MessageBubble from '../chat/message/MessageBubble';
import bot from '../../assets/bot.jpg';
import avatar from '../../assets/avatar.jpg';


const ChatComponent = () => {
  const [t, i18n] = useTranslation('global');
  const [messageHistory, setMessageHistory] = useMessageHistoryAtom(i18n.language);
  const [isQuestionsVisible, setIsQuestionsVisible] = useQuestionToggleAtom();
  const [selectedTab, setSelectedTab] = useSelectedTabAtom();
  const [formButtonEnabled, setFormButtonEnabled] = useFormButtonEnabledAtom();
  const [formInputEnabled, setFormInputEnabled] = useFormInputEnabledAtom();
  const [chatEnvironmentId, ] = useChatEnvironmentAtom();
  const [activeSessionId, setActiveSessionId] = useActiveSessionAtom();
  const [, setAllowStop] = useAtom(allowStopAtom);
  const [, setIsStopping] = useAtom(isStoppingAtom);
  const [selectedQuestion, setSelectedQuestion] = useAtom(selectedQuestionAtom);
  const [tabItemId, ] = useTabItemIdAtom();
  const messageListRef = useRef(null);
  const [bottomSpacerPx, setBottomSpacerPx] = useState(0);
  const inputRef = useRef(null);
  const streamAbortRef = useRef(null);   // AbortController pre práve bežiaci SSE stream
  const titlePendingRef = useRef(null);  // nová konverzácia, ktorá ešte nemá vygenerovaný názov (prvá odpoveď zastavená)
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [ignoreNext, setIgnoreNext] = useState(false);
  const [allowCancel, setAllowCancel] = useState(false);
  const [result, setResult] = useState({});
  const [formData, setFormData] = useState('');
  const [lastSentMessage, ] = useState([
    {
      intent: 'PromptData',
      message: '',
    },
    {
      intent: 'PromptEmbeddings',
      message: '',
    },
    {
      intent: 'PromptGpt',
      message: '',
    },
  ]);

  const prevTabRef = useRef(selectedTab);

  useEffect(() => {
    if (prevTabRef.current === selectedTab) return;
    prevTabRef.current = selectedTab;

    // Tab switched → clear session selection and reset this tab to welcome (new chat) state
    setActiveSessionId(0);

    let initState;
    if (i18n.language === 'sk') initState = messageHistoryAtomSk.init;
    else if (i18n.language === 'cz') initState = messageHistoryAtomCz.init;
    else initState = messageHistoryAtomEn.init;

    const initTabData = initState.find(item => item.intent === selectedTab);
    setMessageHistory(prev => prev.map(item =>
      item.intent === selectedTab && initTabData
        ? { ...item, chat: [...initTabData.chat] }
        : item
    ));
  }, [selectedTab]);

  useEffect(() => () => {
      var messageHistory;
      if (i18n.language === 'sk') {
        messageHistory = messageHistoryAtomSk.init;
      } else if (i18n.language === 'en') {
        messageHistory = messageHistoryAtomEn.init;
      } else if (i18n.language === 'cz') {
        messageHistory = messageHistoryAtomCz.init;
      }
      setMessageHistory(messageHistory);
      setSelectedTab(selectedTabAtom.init);

  },[]);

  useEffect(() => () => {
      var messageHistory;
      if (i18n.language === 'sk') {
        messageHistory = messageHistoryAtomSk.init;
      } else if (i18n.language === 'en') {
        messageHistory = messageHistoryAtomEn.init;
      } else if (i18n.language === 'cz') {
        messageHistory = messageHistoryAtomCz.init;
      }
      setMessageHistory(messageHistory);
      setSelectedTab(selectedTabAtom.init);

  }, []);

  useEffect(() => {
    let currentChat = messageHistory.find(item => item.intent === selectedTab)?.chat;
    let lastMessage = currentChat[currentChat.length - 1];

    // Streamovaná odpoveď (type 3) si pozíciu rieši sama (scroll začiatku k vrchu), takže ju
    // neťaháme dole – inak by rastúci text nepravidelne skákal. Platí počas streamu (streaming)
    // aj po jeho dokončení (wasStreamed) – aby bublinka po dopísaní neskočila na koniec.
    const isStreamingAnswer = lastMessage && (lastMessage.streaming || lastMessage.wasStreamed
      || (lastMessage.Type === 3 && lastMessage.animate));

    const timer = setTimeout(() => {
      if (messageListRef.current && !isStreamingAnswer) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      }
    }, 100);

    if (lastMessage && lastMessage.Type === 'loading') {
      setAllowStop(true);
    } else {
      setAllowStop(false);
    }

    inputRef.current.focus();

    return () => clearTimeout(timer);
  }, [messageHistory, selectedTab]);

  // Dynamická výška spacera pod (práve/naposledy) streamovanou odpoveďou:
  // dopočíta len toľko miesta, aby vrch odpovede vedel vyjsť hore. Dlhá odpoveď, ktorá už
  // vypĺňa okno, dostane iba malý spodný odstup; krátka len obmedzené miesto. Prepočítava sa
  // priebežne (aj počas streamu ako text rastie), takže po dokončení nenastane žiadny skok.
  useLayoutEffect(() => {
    const scroller = messageListRef.current;
    const chat = messageHistory.find(it => it.intent === selectedTab)?.chat;
    const last = chat && chat[chat.length - 1];
    const active = last && (last.streaming || last.wasStreamed);
    if (!active || !scroller) {
      if (bottomSpacerPx !== 0) setBottomSpacerPx(0);
      return;
    }
    const msgs = scroller.querySelectorAll('.msg');
    const lastEl = msgs[msgs.length - 1];
    if (!lastEl) return;
    const ch = scroller.clientHeight;
    const room = ch * 0.42;          // koľko miesta nechať, aby odpoveď vyšla hore (menej = menej prázdna)
    const floor = ch * 0.08;         // vždy aspoň malý odstup nad inputom
    const answerH = lastEl.getBoundingClientRect().height;
    const px = Math.max(floor, Math.round(room - answerH));
    setBottomSpacerPx(prev => (prev !== px ? px : prev));
  }, [messageHistory, selectedTab]);

  useEffect(() => {
    handleResponse(result, ignoreNext);

    // streamovaná odpoveď (type 3 + animate) si scroll rieši sama – neťahať dole
    const isStreamingAnswer = result && result.Type === 3 && result.animate;

    const timer = setTimeout(() => {
      if (messageListRef.current && !isStreamingAnswer) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      }
    }, 500);

    return () => clearTimeout(timer);

  }, [result]);


    useEffect(() => {
      if (!!selectedQuestion) {
        setFormData(selectedQuestion);
        setTimeout(() => {
          handleSubmit(undefined, selectedQuestion);
          setSelectedQuestion('');
        }, 100);
      }
    }, [selectedQuestion]);

  const handleChange = (e) => {
    setFormData(e.target.value);
    setIsInputEmpty(e.target.value.trim() === '');
  };

  const toggleQuestions = () => {
    setIsQuestionsVisible((prevState) => !prevState);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      var data = lastSentMessage.find((item) => item.intent === selectedTab);
      setFormData(data.message);

      setTimeout(() => {
        const inputField = document.querySelector('.msger-input');
        if (inputField) {
          inputField.setSelectionRange(data.message.length, data.message.length);
        }
      }, 0);
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      handleSubmit(e);
    }
  };

  function formatDate(date) {
    const h = '0' + date.getHours();
    const m = '0' + date.getMinutes();

    return `${h.slice(-2)}:${m.slice(-2)}`;
  }

  const handleStop = (e) => {
    e?.preventDefault();
    setIsStopping(true);

    // Zavri bežiaci SSE stream (zruší fetch aj čítačku) -> starý stream už nemôže dopisovať
    // tokeny do (novej) bubliny a socket sa zavrie (spustí serverové zrušenie).
    streamAbortRef.current?.abort();

    // Odstran "loading" bublinu a pridaj info o zastaveni – cez setMessageHistory (nie priama mutacia pola).
    // Rozpísanú (streamovanú) odpoveď nechaj a len ju ukonči, nech nezostane visieť v stave "streaming".
    setMessageHistory(prev => prev.map(tabItem => {
      if (tabItem.intent !== selectedTab) return tabItem;
      const chat = [...tabItem.chat];
      const last = chat[chat.length - 1];
      if (last && last.Type === 'loading') {
        chat.pop();
      } else if (last && last.streaming) {
        chat[chat.length - 1] = { ...last, streaming: false, wasStreamed: true };
      }
      chat.push({
        Message: t('responseStopped'),
        sent: new Date().toISOString(),
        Role: 'user',
        Type: 4,
        id: nanoid()
      });
      return { ...tabItem, chat };
    }));

    // Hned odblokuj UI a schovaj tlacidlo Zastavit (odpoved na pozadi dobehne, ale ignoruje sa cez ignoreNext)
    setAllowStop(false);
    setAllowCancel(false);
    setFormInputEnabled(true);
    setFormButtonEnabled(true);
    setIsStopping(false);
  }

  const handleCancel = (e) => {
      e.preventDefault();

      // stream sa zruší cez abort; ignoreNext je len pre nestreamované odpovede (cez setResult)
      if (!CHAT_INTENT_STREAM_ENDPOINTS[selectedTab]) {
        setIgnoreNext(true);
      }
      handleStop(e);
  }

  const handleSubmit = (e, question) => {
    e?.preventDefault();
    const data = !!question ? question : formData;
    if (!data.length > 0) {
      return;
    }

    setIsInputEmpty(true);
    setFormInputEnabled(false);
    setFormButtonEnabled(false);
    setAllowCancel(true);

    // Zruš prípadný predošlý (ešte bežiaci) stream a priprav čerstvý AbortController pre tento dotaz
    streamAbortRef.current?.abort();
    streamAbortRef.current = new AbortController();

    var selectedObject = lastSentMessage.find((item) => item.intent === selectedTab);
    selectedObject.message = data;

    if (data) {
      const newId = nanoid();
      const newMessageId = newUuid();
      setMessageHistory(
        appenedChatItem(
          selectedTab, {
            Message: data,
            sent: new Date().toISOString(),
            Role: 'assistant',
            Type: 'loading',
            id: nanoid(),
            // okamžitý status počas C# fázy (rephrase ~2-5s, kde je inak len loading) – Python ho potom prepíše
            progress: selectedTab === 'PromptEmbeddings' ? t('analyzingQuestion') : undefined,
          },
          appenedChatItem(
            selectedTab, {
              Message: data,
              sent: new Date().toISOString(),
              Role: 'user',
              Type : 0,
              id: newId,
              MessageID: newMessageId,
              messageId: newMessageId
            },
            messageHistory
          )
        )
      );

      const getMessageParams = (selectedTab, defaultEnv = null, defaultTab = null) => {
        if (selectedTab === 'PromptData' || selectedTab === 'PromptGpt') {
          return { env: defaultEnv, tab: tabItemId };
        }
        if (selectedTab === 'PromptEmbeddings') {
          return { env: chatEnvironmentId, tab: defaultTab };
        }
        return { env: defaultEnv, tab: defaultTab };
      };
      
      const { env, tab } = getMessageParams(selectedTab);
      const sessionTitle = activeSessionId === 0 ? t('newChat') : null;
      const isNewSessionRequest = activeSessionId === 0;

      const onSuccess = (message) => {
        setResult({ ...message, ClientMsgId: newId });
        if (isNewSessionRequest && message.SessionId) {
          setTimeout(() => {
            generateSessionTitle(message.SessionId)
              .then((updatedSession) => {
                const newTitle = updatedSession.title || updatedSession.Title;
                if (newTitle) {
                  window.dispatchEvent(new CustomEvent('sessionTitleGenerated', {
                    detail: { sessionId: message.SessionId, title: newTitle },
                  }));
                }
              })
              .catch(err => console.error('Error generating title:', err));
          }, 2000);
        }
      };

      const onFailure = (error) => {
        console.error(error);
        // Konkrétna, zrozumiteľná hláška podľa kódu chyby (napr. prostredie bez dokumentov);
        // pri ostatných chybách generická hláška.
        const failMessage = (error && error.code === 'no_documents')
          ? t('noDocuments')
          : t('defaultEx');
        setMessageHistory(prev => prev.map(tabItem => {
          if (tabItem.intent !== selectedTab) return tabItem;
          const chat = [...tabItem.chat];
          if (chat.length > 0 && chat[chat.length - 1].Type === 'loading') {
            chat.pop();
          }
          chat.push({
            Message: failMessage,
            sent: new Date().toISOString(),
            Role: 'assistant',
            Type: 0,
            id: nanoid(),
          });
          return { ...tabItem, chat };
        }));
      };

      const onComplete = () => {
        if (isNewSessionRequest) {
          window.dispatchEvent(new Event('chatSent'));
        }
        setFormInputEnabled(true);
        setFormButtonEnabled(true);
        setAllowCancel(false);
      };

      if (CHAT_INTENT_STREAM_ENDPOINTS[selectedTab]) {
        // streamovaný (SSE) request – krokový progres + postupné vypisovanie odpovede.
        // Dokumenty = bublina type 3 (zdroje), všeobecný chat = type 0 (zvýraznenie kódu).
        const isDocs = selectedTab === 'PromptEmbeddings';
        const answerType = isDocs ? 3 : 0;
        sendMessageToBEStream(selectedTab, messageHistory, data, newId, env, tab, activeSessionId, newMessageId, sessionTitle, {
          signal: streamAbortRef.current.signal,
          // id novej konverzácie hneď na začiatku – keď používateľ odpoveď zastaví, ďalšia otázka
          // pokračuje v tej istej konverzácii (inak by vznikla nová a stará by ostala osirelá)
          onSession: (sessionId) => {
            if (!isNewSessionRequest) return;
            titlePendingRef.current = sessionId;
            setActiveSessionId(sessionId);
            window.dispatchEvent(new Event('chatSent'));
          },
          onStep: (step) => {
            if (!step || !isDocs) return;   // všeobecný chat: len bodky do prvého tokenu
            const label = step.status === 'start'
              ? t('starting')
              : (step.tool_name === 'retriever' ? t('searchingDocs') : t('generatingAnswer'));
            updateLoadingProgress(label, step.step);
          },
          onToken: (delta) => {
            if (!delta) return;
            setMessageHistory(prev => prev.map(tabItem => {
              if (tabItem.intent !== selectedTab) return tabItem;
              const chat = [...tabItem.chat];
              const last = chat[chat.length - 1];
              if (last && (last.Type === 'loading' || last.streaming)) {
                const prevText = last.streaming ? last.Message : '';
                chat[chat.length - 1] = {
                  ...last, Type: answerType, Role: 'assistant', streaming: true,
                  animate: false, Message: prevText + delta,
                };
              }
              return { ...tabItem, chat };
            }));
          },
          // Finalizuj streamovanú bublinu NA MIESTE (rovnaké id → žiadny remount ani skok na koniec).
          onDone: (message) => {
            if (!message) { onFailure('No messages returned'); return; }
            setMessageHistory(prev => prev.map(tabItem => {
              if (tabItem.intent !== selectedTab) return tabItem;
              const chat = withDbIds(tabItem.chat, newId, message);
              const i = chat.length - 1;
              const last = chat[i];
              if (last && (last.streaming || last.Type === 'loading')) {
                const finalText = (typeof message.Message === 'string' && message.Message.length)
                  ? message.Message : last.Message;
                chat[i] = {
                  ...last,
                  Type: answerType,
                  Role: 'assistant',
                  streaming: false,
                  wasStreamed: true,   // aby auto-scroll nechal bublinu na mieste (žiadny skok)
                  animate: false,
                  Message: finalText,
                  MessageID: message.MessageID,
                  Metadata: message.Metadata,
                  Rating: message.Rating ?? null,
                  EnvironmentType: message.EnvironmentType,
                };
              }
              return { ...tabItem, chat };
            }));
            if (message.SessionId) {
              setActiveSessionId(message.SessionId);
              window.dispatchEvent(new Event('chatSent'));
            }
            // názov pri prvej DOKONČENEJ odpovedi (aj keď prvú odpoveď používateľ zastavil)
            if (message.SessionId && (isNewSessionRequest || titlePendingRef.current === message.SessionId)) {
              titlePendingRef.current = null;
              setTimeout(() => {
                generateSessionTitle(message.SessionId)
                  .then((updatedSession) => {
                    const newTitle = updatedSession.title || updatedSession.Title;
                    if (newTitle) {
                      window.dispatchEvent(new CustomEvent('sessionTitleGenerated', {
                        detail: { sessionId: message.SessionId, title: newTitle },
                      }));
                    }
                  })
                  .catch(err => console.error('Error generating title:', err));
              }, 2000);
            }
          },
          onError: (err) => onFailure(err),
        }).finally(onComplete);
      } else {
        sendMessageToBE(selectedTab, messageHistory, data, newId, env, tab, activeSessionId, newMessageId, sessionTitle)
          .then(onSuccess)
          .catch(onFailure)
          .finally(onComplete);
      }
    }

    setAllowStop(false);
    setIsStopping(false);

    setIsInputEmpty(true);
    setFormData('');
  };

  // Klientske id otázky / uvítania -> DB id. React kľúč (uiKey) ostáva pôvodný – inak by React
  // bublinu zahodil a vykreslil nanovo (prebliknutie uvítania a otázky po odpovedi).
  const withDbIds = (chat, clientMsgId, message) => chat.map((c, index) => {
    let updatedC = c;
    if (c.id === clientMsgId && message.UserMessageID) {
      updatedC = { ...updatedC, uiKey: updatedC.uiKey || updatedC.id, id: message.UserMessageID };
    }
    if (index === 0 && message.WelcomeMessageID && typeof updatedC.id !== 'number') {
      updatedC = { ...updatedC, uiKey: updatedC.uiKey || updatedC.id, id: message.WelcomeMessageID };
    }
    return updatedC;
  });

  const removeLoading = (messageHistory, selectedTab) => {
    const chat = messageHistory.find(item => item.intent === selectedTab)?.chat;
    const lastMessage = chat[chat.length - 1];
    if (lastMessage && (lastMessage.Type === 'loading' || lastMessage.streaming)) {
      chat.pop();
    }
  }

  // Aktualizuje text "loading" bubliny počas streamovania (krokový progres)
  const updateLoadingProgress = (progressText, stepCount) => {
    setMessageHistory(prev => prev.map(tabItem => {
      if (tabItem.intent !== selectedTab) return tabItem;
      const chat = [...tabItem.chat];
      const last = chat[chat.length - 1];
      if (last && last.Type === 'loading') {
        chat[chat.length - 1] = { ...last, progress: progressText, stepCount: stepCount };
      }
      return { ...tabItem, chat };
    }));
  }

  const handleResponse = (message, ignoreNext = false) => {
    if (message && message.SessionId) {
      setActiveSessionId(message.SessionId);
      // Refresh history after every successful response carrying a session id.
      window.dispatchEvent(new Event('chatSent'));
    }
    if (ignoreNext) {
      setIgnoreNext(false);
      return;
    }

    removeLoading(messageHistory, selectedTab, message.newId);

    if(message.Type === 0 && message.Status === 'ERROR' && message.Message != undefined){
      message.Message = t(message.Message);
    }

    if(message.Type === 4 && message.Message != undefined){
      message.Message = t(message.Message);
    }

    if (message.Type === 0) {
      console.log('type text');
      setMessageHistory(prev => prev.map(tabItem => {
         if (tabItem.intent === selectedTab) {
             const newChat = withDbIds(tabItem.chat, message.ClientMsgId, message);
             newChat.push({
                Message: message.Message,
                sent: new Date().toISOString(),
                Role: message.Role,
                Type: message.Type,
               Rating: message.Rating ?? null,
                GeneratedSQL: message.GeneratedSQL,
                id: message.MessageID || nanoid(),
             });
             return { ...tabItem, chat: newChat };
         }
         return tabItem;
      }));
    }
    else if (message.Type === 1) {
      setMessageHistory(prev => prev.map(tabItem => {
         if (tabItem.intent === selectedTab) {
             const newChat = withDbIds(tabItem.chat, message.ClientMsgId, message);
             newChat.push({
                Table: { Rows: message.Message.Rows, Columns: message.Message.Columns },
                SystemMessage: message.SystemMessage,
                GeneratedSQL: message.GeneratedSQL,
                sent: new Date().toISOString(),
                Role: message.Role,
                Type: message.Type,
               Rating: message.Rating ?? null,
                id: message.MessageID || nanoid(),
             });
             return { ...tabItem, chat: newChat };
         }
         return tabItem;
      }));
    }
    else if (message.Type === 2) {
      console.log(message);
      setMessageHistory(
        appenedChatItem(
          selectedTab,
          {
            Message: message.Message,
            sent: new Date().toISOString(),
            Role: message.Role,
            Type: message.Type,
            id: nanoid(),
          },
          messageHistory
        )
      );
    }
    else if (message.Type === 3) {
      setMessageHistory(
        appenedChatItem(
          selectedTab,
          {
            Message: message.Message,
            MessageID : message.MessageID,
            sent: new Date().toISOString(),
            Role: message.Role,
            Type: message.Type,
            Rating: message.Rating ?? null,
            EnvironmentType: message.EnvironmentType,
            id: nanoid(),
            Metadata: message.Metadata,
            animate: message.animate
          },
          messageHistory
        )
      );
    }
    else if (message.Type === 4) {
      setMessageHistory(
        appenedChatItem(
          selectedTab,
          {
            Message: message.Message,
            sent: new Date().toISOString(),
            Role: message.Role,
            Type: message.Type,
            id: message.MessageID || nanoid(),
          },
          messageHistory
        )
      );
    }
  }

  const currentTabChat = messageHistory.find(it => it.intent === selectedTab)?.chat;
  const lastMsgInTab = currentTabChat && currentTabChat[currentTabChat.length - 1];
  // Spacer od štartu streamu (nie počas loading) – dáva miesto pre scroll, aby odpoveď
  // vedela vyjsť hore; jeho výšku dynamicky počíta useLayoutEffect (bottomSpacerPx).
  const showBottomSpacer = !!(lastMsgInTab && (lastMsgInTab.streaming || lastMsgInTab.wasStreamed));

  return (
    <div ref={messageListRef} className={`context-wrap focus`}>
      <main className="msger-chat">
        {messageHistory
          .filter((item) => item.intent === selectedTab)
          .map((item) =>
            item.chat.map((item, idx, arr) => (
              item.Type === 5 ? (
                <div className="reset-separator" key={item.uiKey || item.id}>
                  <span className='reset-text'>{item.Message}</span>
                </div>
              ) : (
              <MessageBubble
                key = {item.uiKey || item.id}
                id = {item.id}
                type = {item.Type}
                envType = {item.EnvironmentType}
                message = {item.Message ? item.Message : item.Table}
                messageId = {item.MessageID}
                rating = {item.Rating}
                meta = {item.Metadata}
                sent = {item.sent}
                role = {item.Role}
                generatedSQL = {item.GeneratedSQL}
                progress = {item.progress}
                stepCount = {item.stepCount}
                animate = {item.animate}
                streaming = {item.streaming}
                isLast = {idx === arr.length - 1}
                bot = {bot}
                avatar = {avatar}
              />
              )
            ))
          )}

          {/* Spacer pod (práve) streamovanou odpoveďou – miesto pre scroll, aby odpoveď vedela vyjsť hore. */}
          {showBottomSpacer ?
              <div className="chat-bottom-spacer" aria-hidden="true" style={{ minHeight: bottomSpacerPx + 'px' }} /> : null }
      </main>


      <form className="chat-form" onSubmit={handleSubmit}>
        <div
          id="questionButton"
          className={`question-list-button ${isQuestionsVisible ? 'clicked' : ''}`}
          onClick={toggleQuestions}
        >
          <span className="material-symbols-outlined">list</span>
        </div>
          <TextArea
            value={formData}
            disabled={!formInputEnabled}
            autoComplete="off"
            onChange={handleChange}
            onKeyDown={handleInputKeyDown}
            onEnterPress={handleSubmit}
            ref={inputRef}
          />
          {allowCancel ? (
            // počas odpovede je na mieste Odoslať tlačidlo Stop – vždy viditeľné, aj pri dlhej odpovedi
            <button
              type="button"
              className="msger-send-btn msger-stop-btn"
              onClick={handleCancel}
              title={t('stopResponding')}
            >
              <span className="material-symbols-outlined">stop</span>
              <span className="send-text">{t('stop')}</span>
            </button>
          ) : (
          <button
            type="submit"
            className={`msger-send-btn ${isInputEmpty ? 'hidden' : ''}`}
            disabled={!formButtonEnabled}
          >
            {formInputEnabled ? (
              <>
              <span className="material-symbols-outlined">arrow_upward_alt</span>
              <span className="send-text">{t('send')}</span>
              </>
            ) : (
              <>
              <span className="material-symbols-outlined">hourglass_empty</span>
              <span className="send-text">{t('working')}</span>
              </>
            )}
          </button>
          )}
      </form>
    </div>
  );
};

export default ChatComponent;
